/* eslint-disable arrow-body-style */
import React, { useEffect, useImperativeHandle, useMemo, useState, forwardRef, useRef } from 'react';
import ReactDOM from 'react-dom';
import { getChartComData, updateFilterInfoVal, extractMetricDataKey, extractYAxisIndex } from './_utils';
import TabComponent from '../TabComponent';
import AIChartContent from '../AIChartContent';
import './index.scss';
import {
  FORMAT_MAP,
  METRIC_DISPLAY_NAME,
  SMALL_VAL_METRIC,
  TOOLTIPS_DATE_FORMAT_MAP,
  TOOLTIPS_FORMAT_MAP,
  YAXIS_FORMAT_MAP,
} from '../../../config/constant';
import { TAIContent, TYAxisItem, IAIContentRef } from './_type';
import { useBIData } from './hooks';
import EllipsisHover from '@gp/components/EllipsisHover';
import { Bubble, Text } from 'tea-component';
import { useIntl } from 'umi';
import dataBrainIcon from '../../../svg/databrain-logo.svg';
import classNames from 'classnames';
import FilterComponent from '../FilterComponent';
import SvgIcon from '@home/components/SvgIcon';
import CopilotEllipsisText from '@/pages/aigc/pages/Agent/ChatPage/components/CopilotEllipsisText/CopilotEllipsisText';
import CommonBubble from '@home/components/CommonBubble';
import { getFormatRule } from '@/pages/aigc/utils/utils';
import useCssFullScreen from './hooks/useCssFullScreen';
import { useDebounceEffect, useSize } from 'ahooks';



const AIContent = forwardRef<IAIContentRef, TAIContent>((props: TAIContent, ref) => {
  const {
    filter_info: filterInfo,
    dimension_info: dimensionInfo,
    data: propsData,
    title,
    xAxis,
    yAxis,
    metrics_info: metricsInfo,
    legends,
    chat_type,
    system: systemType,
    hideSource = false,
    isCopilot = true,
    chartHeight,
    additional_table_field,
    filter_default_value,
    chartDataId = '',
    // 外部传入的回调函数
    onFilterChange,
    onMetricChange,
    // 默认开启初始化设置指标、筛选器
    defaultShowMetrics = true,
    // 默认开启初始化设置筛选器值
    defaultShowFilters = true,
  } = props;
  const { formatMessage, locale } = useIntl();
  
  // 筛选器/Tab 容器的 ref，用于监听高度变化
  const tabContainerRef = useRef<HTMLDivElement>(null);
  const tabContainerSize = useSize(tabContainerRef);
  
  const [filterInfoVal, setFilterInfoVal] = useState<{
    [key: string]: string;
  } | null>({});
  const { innerFilterInfoVal, showMetrics, showFilters } = useBIData({
    filterInfo,
    dimensionInfo,
    filterInfoVal,
    metricsInfo,
    filter_default_value,
  });

  const [currentMetric, setCurrentMetric] = useState<string>('');
  const [isHoverInfoAlertIcon, setIsHoverInfoAlertIcon] = useState(false);
  
  // 管理图表类型状态，防止Copilot全屏时Portal导致状态丢失
  const [chartType, setChartType] = useState<string>('');
  
  // 使用 CSS 全屏功能（不使用浏览器原生 API，避免层叠上下文问题）
  const fullScreenContent = useRef<HTMLDivElement>(null);
  const { isFullScreen, requestFullScreen, exitFullScreen } = useCssFullScreen(fullScreenContent);

  // 全屏状态下，监听筛选器容器高度变化，触发 resize 让 ECharts 重绘
  useDebounceEffect(
    () => {
      if (isFullScreen) {
        const resizeEvent = new Event('resize');
        window.dispatchEvent(resizeEvent);
      }
    },
    [isFullScreen, tabContainerSize?.height],
    {
      wait: 50,
    },
  );
  
  // 设置初始值
  useEffect(() => {
    // 优先使用filter_default_value中的metric设置
    const defaultMetric = filter_default_value?.metric;
    if (defaultShowMetrics) {
      if (defaultMetric && showMetrics.some(m => extractMetricDataKey(m?.data_key) === defaultMetric || (m as any)?.metric_key === defaultMetric)) {
        setCurrentMetric(defaultMetric);
      } else {
        setCurrentMetric(extractMetricDataKey(showMetrics?.[0]?.data_key) || showMetrics?.[0]?.metric_key || '' as string);
      }
    }
    // showMetrics?.toString() 避免filterInfoVal更新时重新渲染组件，重新赋值showMetrics导致重新设置metrics
  }, [JSON.stringify(showMetrics || {}), filter_default_value?.metric, defaultShowMetrics]);

  // 通过 useImperativeHandle 暴露方法给父组件
  useImperativeHandle(ref, () => ({
    setFilterInfoVal,
    setCurrentMetric,
    getShowMetrics: () => showMetrics,
    getFilterInfoVal: () => filterInfoVal,
    getCurrentMetric: () => currentMetric,
  }));

  // 设置初始筛选值
  useEffect(() => {
    defaultShowFilters && setFilterInfoVal(innerFilterInfoVal);
  }, [JSON.stringify(innerFilterInfoVal || {}), defaultShowFilters]);
  
  const metricObj = useMemo(() => {
    return metricsInfo?.find((item) => {
      const metricKey = extractMetricDataKey(item.data_key) || item.metric_key || '';
      return metricKey === currentMetric;
    });
  }, [metricsInfo, currentMetric]);
  const chartComData = useMemo(
    () =>
      getChartComData({
        filterInfoVal,
        inputData: propsData,
        xAxis,
        // yAxis,
        inputMetricsInfo: metricsInfo,
        inputLegends: legends,
        // chartType: chat_type,
        dimensionInfo,
        metric: currentMetric,
        metricName:
          metricObj?.name ||
          METRIC_DISPLAY_NAME?.[currentMetric] ||
          currentMetric ||
          '',
        additional_table_field,
      }),
    [
      filterInfoVal,
      propsData,
      xAxis,
      metricsInfo,
      legends,
      dimensionInfo,
      currentMetric,
      metricObj?.name,
      additional_table_field,
    ],
  );

  const { formatter, yAxisFormatter, toolTipsFormatter, toolTipsDateFormatter } = useMemo(() => {
    const { type: dataFormatType, data_key, format: metricFormat } = metricObj || {};
    const actualDataKey = extractMetricDataKey(data_key);
    // date类型进行format处理，time类型原数据就是time，其他维度暂不处理，因为当前浮窗标题为时间，如果不是事件则展示原值
    const dateDimensionFormat = dimensionInfo?.find((item) => item.data_key === "date")?.toolTipsFormat || dimensionInfo?.find((item) => item.data_key === "date")?.format;
    return {
      // formatter 好像没用到了，当前图表（浮窗）展示用的toolTipsFormatter，表格用的在上层处理好的column_date_type
      formatter: getFormatRule(metricFormat) || FORMAT_MAP?.[dataFormatType] || dataFormatType,
      yAxisFormatter: SMALL_VAL_METRIC?.includes(actualDataKey) ? YAXIS_FORMAT_MAP.smallType : (YAXIS_FORMAT_MAP?.[dataFormatType] || dataFormatType),
      toolTipsFormatter: getFormatRule(metricFormat) || TOOLTIPS_FORMAT_MAP?.[dataFormatType] || dataFormatType,
      toolTipsDateFormatter: getFormatRule(dateDimensionFormat) || TOOLTIPS_DATE_FORMAT_MAP?.[filterInfoVal?.granularity] || 'day',
    };
  }, [dimensionInfo, filterInfoVal?.granularity, metricObj]);

  // 从 yAxis 中提取配置
  const yAxisConfig = useMemo(() => {
    if (!yAxis || !Array.isArray(yAxis) || yAxis.length === 0) {
      return null;
    }
    // 如果 yAxis 是字符串数组，返回 null
    if (typeof yAxis?.[0] === 'string') {
      return null;
    }
    const targetIndex = Math.max(
      0,
      Math.min(
        yAxis.length - 1,
        extractYAxisIndex(metricObj?.data_key)
      )
    );
    const targetYAxis = yAxis?.[targetIndex] as TYAxisItem | undefined;
    return targetYAxis || null;
  }, [yAxis, metricObj]);

  const onChange = (data: { [key: string]: string; }, isMetrics = false) => {
    if (!isMetrics) {
      const newFilterInfoVal = updateFilterInfoVal(
        filterInfoVal,
        filterInfo,
        data,
      );
      setFilterInfoVal(newFilterInfoVal);
      // 通知外部筛选值变化
      onFilterChange?.(newFilterInfoVal, currentMetric);
    } else {
      setCurrentMetric(data?.metrics);
      // 通知外部指标变化
      onMetricChange?.(data?.metrics, filterInfoVal);
    }
  };
  // 单数据源名称
  const singleDataSourceName = useMemo(() => {
    const isEn = locale === "en-US";
    const dataArray = chartComData?.tableInfo?.data;
    
    if (!dataArray || dataArray?.length === 0) {
      return { displayName: '', originalName: '' };
    }
    
    // 获取第一个有 source 的数据项
    const firstValue = dataArray.find((item) => item?.source);
    if (!firstValue) {
      return { displayName: '', originalName: '' };
    }
    
    // 检查所有数据项的 source 是否都相同
    const allSameSource = dataArray.every((item) => item?.source === firstValue.source);
    // 只有当所有 source 都相同时，才返回数据源信息
    if (allSameSource) {
      const originalName = firstValue?.source;
      const displayValue = originalName === 'databrain' ?  '' : firstValue?.source_display;
      return {
        displayName: displayValue ? `${isEn ? ', ' : '，'}${displayValue}` : '',
        originalName: originalName || ''
      };
    }
    
    return { displayName: '', originalName: '' };
  }, [chartComData?.tableInfo, locale]);

  const system = useMemo(() => {
    const originName = singleDataSourceName?.originalName;
    if (systemType === 'Intelligence' && (['databrain'].includes(originName))) {
      return `Intelligence_new`;
    }
    return systemType;
  }, [singleDataSourceName?.originalName, systemType]);

  // 全屏按钮插槽
  const fullScreenSlot = useMemo(() => {
    return (
      <CommonBubble
        dark
        placement="top"
        content={formatMessage({ id: 'dba_full_screen' })}
      >
        <Text 
          className="table-btn" 
          onClick={ ()=> isFullScreen ? exitFullScreen() :  requestFullScreen()}
        >
          <SvgIcon 
            className="table-icon" 
            type={isFullScreen ? "clonse" : "fullscreen" }
          />
        </Text>
      </CommonBubble>
    );
  }, [isFullScreen, requestFullScreen, exitFullScreen, formatMessage]);

  // 主内容渲染
  // 注意：不使用useMemo包裹，因为过多的依赖项会导致全屏切换时重新创建元素
  // 状态已经通过受控模式（chartType）提升到父组件，React的diff算法会保持组件实例
  const contentElement = (
    <div 
      ref={fullScreenContent}
      className={classNames(
        'basic-cardbox table-line dba-agent-chart-container',
        isCopilot && 'dba-agent-copilot-chart-container',
        isFullScreen && 'dba-agent-chart-fullscreen'
      )} 
      contentEditable={false}
    >
      <div className="basic-cardbox-body">
        <div className="dba-may-ask-chart-content">
          <AIChartContent
            key={chartDataId}
            activeChartType={chartType}
            onActiveChartTypeChange={(type) => {
              setChartType(type);
            }}
            middleContentCom={
              isCopilot && !isFullScreen ?
                <FilterComponent
                  ref={tabContainerRef}
                  onChange={onChange}
                  filterInfoVal={filterInfoVal}
                  metricsInfo={metricsInfo}
                  currentMetric={currentMetric}
                  dimensionInfo={dimensionInfo}
                  showMetrics={showMetrics}
                  showFilters={showFilters}
                  isFullScreen={isFullScreen}
                /> : <TabComponent
                  ref={tabContainerRef}
                  onChange={onChange}
                  filterInfoVal={filterInfoVal}
                  metricsInfo={metricsInfo}
                  currentMetric={currentMetric}
                  dimensionInfo={dimensionInfo}
                  showMetrics={showMetrics}
                  showFilters={showFilters}
                />
            }
            subTitleCom={isCopilot ?  
              <CopilotEllipsisText 
                text={title}
                suffix={
                  <CommonBubble
                    placement="top"
                    closeOnScroll
                    destroyOnClose
                    visible={isHoverInfoAlertIcon}
                    content={
                      <div className="dba-source-bubble">
                        {formatMessage({ id: 'dba_access_table_hover' })}
                        <img src={dataBrainIcon} alt="" />
                        {formatMessage({ id: `dba_access_table_${system}` })}
                        {singleDataSourceName.displayName}
                      </div>
                    }
                    overlayClassName='dba-nav-menu-overlay dba-copilot-source-overlay'
                    className='dba-source-bubble-box'
                  > 
                    <span 
                      onMouseEnter={() => setIsHoverInfoAlertIcon(true)} 
                      onMouseLeave={() => setIsHoverInfoAlertIcon(false)}
                    >
                      <SvgIcon type='alert-circle-m' /> 
                    </span>         
                  </CommonBubble>
                }
              />
              :
              <div className="dba-table-title-box">
                <EllipsisHover
                  text={title}
                  bubbleText={title}
                  className={classNames(
                    'basic-cardbox-title table-title dba-title',
                  )}
                />
                
                {(system && !hideSource) &&  (
                  <Bubble
                    content={
                      <div className="dba-source-bubble">
                        {formatMessage({ id: 'dba_access_table_hover' })}
                        <img src={dataBrainIcon} alt="" />
                        {formatMessage({ id: `dba_access_table_${system}` })}
                        {singleDataSourceName.displayName}
                      </div>
                    }
                    overlayClassName='dba-nav-menu-overlay'
                    className='dba-source-bubble-box'
                  >
                    <div className="dba-source-tip">
                      {formatMessage({ id: 'dba_access_table' })}
                    </div>
                  </Bubble>
                )}
              </div>
            }
            isCopilot={isCopilot && !isFullScreen}
            filterVal={filterInfoVal ?? {}}
            data={chartComData}
            formatter={formatter}
            yAxisFormatter={yAxisFormatter}
            toolTipsFormatter={toolTipsFormatter}
            metricObj={metricObj}
            tableInfo={chartComData?.tableInfo}
            downloadTitle={title}
            chatType={chat_type}
            xAxis={xAxis}
            height={isFullScreen ? window.innerHeight - (tabContainerSize?.height || 0) - (isCopilot ? 70 : 120) : (chartHeight || 290)}
            toolTipsDateFormatter={toolTipsDateFormatter}
            chartDataId={chartDataId}
            showFilters={showFilters}
            showMetrics={showMetrics}
            dimensionInfo={dimensionInfo}
            rawData={propsData}
            metricsInfo={metricsInfo}
            legends={legends}
            filterInfo={filterInfo}
            yAxisConfig={yAxisConfig}
            additional_table_field={additional_table_field}
            operationSlot={fullScreenSlot}
            requestFullScreen={requestFullScreen}
            exitFullScreen={exitFullScreen}
            isFullScreen={isFullScreen}
          />
        </div>
      </div>
    </div>
  );

  // Copilot模式全屏时Portal到body，避免拖拽容器的transform影响
  if (isCopilot && isFullScreen) {
    return (
      <>
        <div className="dba-agent-chart-fullscreen-backdrop" />
        {ReactDOM.createPortal(contentElement, document.body)}
      </>
    );
  }

  // 非全屏时正常渲染
  return (
    <>
      {isFullScreen && <div className="dba-agent-chart-fullscreen-backdrop" />}
      {contentElement}
    </>
  );
});

export default React.memo(AIContent);
